import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Baby, CheckCircle2, X, Plus, Trash2, CreditCard, Camera, Loader2, Check, MessageCircle, BadgePercent, FileText, Upload } from "lucide-react";
import { api, resolveImg, uploadPublicFile, uploadDocument, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { money } from "@/lib/currency";

const newChild = (program) => ({
  uid: (crypto.randomUUID && crypto.randomUUID()) || String(Math.random()),
  name: "", dob: "", gender: "", photo_url: "", allergies: "", grade: "", program: program || "sunday",
  jewish: "", conversion_doc: "", answers: {}, russian_group: "",
});

const OTHER_LABEL = { en: "Other", cs: "Jiné", he: "אחר", ru: "Другое" };
const OTHER_PH = { en: "Please specify…", cs: "Upřesněte prosím…", he: "פרטו…", ru: "Уточните…" };
const YESNO = { yes: { en: "Yes", cs: "Ano", he: "כן", ru: "Да" }, no: { en: "No", cs: "Ne", he: "לא", ru: "Нет" } };

const qLabelEn = (q) => (q.label && (q.label.en || Object.values(q.label).find(Boolean))) || "Question";
const optLabelEn = (o) => (o.label && (o.label.en || Object.values(o.label).find(Boolean))) || o.id;

function answerReadable(q, v) {
  if (q.type === "text") return (v || "").trim();
  if (q.type === "yesno") return v === "yes" ? "Yes" : v === "no" ? "No" : "";
  const sel = (v && v.sel) || [];
  const labels = sel.filter((id) => id !== "__other__").map((id) => optLabelEn((q.options || []).find((o) => o.id === id) || {})).filter(Boolean);
  if (sel.includes("__other__") && (v?.other || "").trim()) labels.push(`Other: ${v.other.trim()}`);
  return labels.join(", ");
}
function buildAnswers(questions, ansObj) {
  return questions.map((q) => ({ q: qLabelEn(q), a: answerReadable(q, ansObj[q.id]) })).filter((x) => x.a);
}
function isAnswered(q, v) {
  if (q.type === "text") return !!(v || "").trim();
  if (q.type === "yesno") return v === "yes" || v === "no";
  const sel = (v && v.sel) || [];
  if (!sel.length) return false;
  if (sel.length === 1 && sel[0] === "__other__") return !!(v?.other || "").trim();
  return true;
}

// Renders one custom question (text / yes-no / dropdown / multiselect with optional "Other").
function QuestionField({ q, value, onChange, lang, idPrefix }) {
  const label = (q.label && (q.label[lang] || q.label.en)) || "";
  const tid = `${idPrefix}-q-${q.id}`;
  const req = q.required ? <span className="text-[#BE923C]"> *</span> : null;
  if (q.type === "text") {
    return (
      <div data-testid={tid}>
        <Label className="text-xs">{label}{req}</Label>
        <Input data-testid={`${tid}-input`} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 border-[#0F172A]/10 bg-white" />
      </div>
    );
  }
  if (q.type === "yesno") {
    return (
      <div data-testid={tid}>
        <Label className="text-xs">{label}{req}</Label>
        <div className="mt-1.5 flex gap-2">
          {["yes", "no"].map((yn) => (
            <button key={yn} type="button" data-testid={`${tid}-${yn}`} onClick={() => onChange(yn)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${value === yn ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#475569] border-[#0F172A]/15 hover:border-[#BE923C]"}`}>
              {YESNO[yn][lang] || YESNO[yn].en}
            </button>
          ))}
        </div>
      </div>
    );
  }
  // dropdown (single) or multiselect (multi)
  const multi = q.type === "multiselect";
  const v = value && typeof value === "object" ? value : { sel: [], other: "" };
  const sel = v.sel || [];
  const toggle = (id) => {
    let next;
    if (multi) next = sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id];
    else next = sel.includes(id) ? [] : [id];
    onChange({ ...v, sel: next });
  };
  const otherOn = sel.includes("__other__");
  return (
    <div data-testid={tid}>
      <Label className="text-xs">{label}{req}{multi && <span className="text-[#94A3B8] font-normal"> · {{ en: "select all that apply", cs: "vyberte vše, co platí", he: "ניתן לבחור כמה", ru: "можно выбрать несколько" }[lang]}</span>}</Label>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {(q.options || []).map((o) => {
          const active = sel.includes(o.id);
          return (
            <button key={o.id} type="button" data-testid={`${tid}-opt-${o.id}`} onClick={() => toggle(o.id)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${active ? "bg-[#BE923C] text-white border-[#BE923C]" : "bg-white text-[#475569] border-[#0F172A]/15 hover:border-[#BE923C]"}`}>
              {(o.label && (o.label[lang] || o.label.en)) || o.id}
            </button>
          );
        })}
        {q.allow_other && (
          <button type="button" data-testid={`${tid}-opt-other`} onClick={() => toggle("__other__")}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${otherOn ? "bg-[#BE923C] text-white border-[#BE923C]" : "bg-white text-[#475569] border-[#0F172A]/15 hover:border-[#BE923C]"}`}>
            {OTHER_LABEL[lang] || OTHER_LABEL.en}
          </button>
        )}
      </div>
      {q.allow_other && otherOn && (
        <Input data-testid={`${tid}-other`} value={v.other || ""} placeholder={OTHER_PH[lang] || OTHER_PH.en}
          onChange={(e) => onChange({ ...v, other: e.target.value })} className="mt-2 border-[#0F172A]/10 bg-white max-w-xs" />
      )}
    </div>
  );
}


function ageFromDob(dob) {
  if (!dob) return null;
  const b = new Date(dob);
  if (isNaN(b)) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
  return age >= 0 && age < 120 ? age : null;
}

// --- Class-group allocation (Sunday Hebrew School) ---
const RUSSIAN_RE = /(russ|русск|rušt|rustin|rustí|rušti)/i;
const RU_GROUP_Q = { en: "Would your child like to join the Russian-speaking group?", cs: "Chtělo by vaše dítě chodit do rusky mluvící skupiny?", he: "האם ילדכם רוצה להצטרף לקבוצה דוברת רוסית?", ru: "Хотел бы ваш ребёнок присоединиться к русскоязычной группе?" };
const GROUP_LABEL = { en: "Class group", cs: "Skupina", he: "קבוצה", ru: "Группа" };
const GROUP_REVIEW = { en: "We'll confirm the right group with you", cs: "Správnou skupinu vám potvrdíme", he: "נתאם איתכם את הקבוצה המתאימה", ru: "Мы согласуем подходящую группу с вами" };

function labelHasRussian(label) {
  return !!(label && Object.values(label).some((s) => RUSSIAN_RE.test(String(s || ""))));
}
function childSpeaksRussian(childQuestions, ans) {
  for (const q of childQuestions || []) {
    if (q.type === "multiselect" || q.type === "dropdown" || q.type === "select") {
      const v = (ans || {})[q.id];
      let sel = [];
      if (v && Array.isArray(v.sel)) sel = v.sel;
      else if (typeof v === "string" && v) sel = [v];
      for (const id of sel) {
        const o = (q.options || []).find((x) => x.id === id);
        if (o && labelHasRussian(o.label)) return true;
      }
    }
  }
  return false;
}
function ageAtSchoolStart(dob, mmdd) {
  if (!dob) return null;
  const b = new Date(dob);
  if (isNaN(b)) return null;
  const parts = (mmdd || "10-01").split("-").map((n) => parseInt(n, 10));
  const now = new Date();
  const ref = new Date(now.getFullYear(), (parts[0] || 10) - 1, parts[1] || 1);
  let age = ref.getFullYear() - b.getFullYear();
  if (ref.getMonth() < b.getMonth() || (ref.getMonth() === b.getMonth() && ref.getDate() < b.getDate())) age -= 1;
  return age >= 0 && age < 120 ? age : null;
}
function allocateGroup(cfg, kid, speaksRu) {
  if (cfg.russian_group_enabled !== false && speaksRu && kid.russian_group === "yes") {
    return { name: cfg.russian_group_name || "Group 4 · Russian speakers", review: false };
  }
  const age = ageAtSchoolStart(kid.dob, cfg.school_year_start || "10-01");
  if (age != null) {
    for (const g of (cfg.age_groups || [])) {
      if (Number(g.min_age) <= age && age <= Number(g.max_age)) return { name: g.name || "Group", review: false };
    }
    return { name: "", review: true };
  }
  return { name: "", review: false };
}

export function ChildProgramSignup({ defaultProgram = "sunday", returnPath, compact = false }) {
  const { t, lang } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cfg, setCfg] = useState({ payment_mode: "none", payment_enabled: false, price_sunday: 0, price_thursday: 0, price_both: 0, currency: "czk", paypal_enabled: false, texts: {}, suggested_note: {}, photo_sunday: "", photo_thursday: "", photo_both: "", sibling_enabled: true, sibling_type: "percent", sibling_value: 20, jewish_questions_enabled: true, age_groups: [], russian_group_enabled: true, russian_group_name: "", school_year_start: "10-01" });
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [paidDone, setPaidDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [payLoading, setPayLoading] = useState("");
  const [uploadingUid, setUploadingUid] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState("");
  const [contribution, setContribution] = useState(0);
  const [contribTouched, setContribTouched] = useState(false);
  const [parent, setParent] = useState({ parent_email: "", parent_address: "", photo_consent: false, father_name: "", father_phone: "", father_jewish: "", father_conversion_doc: "", mother_name: "", mother_phone: "", mother_jewish: "", mother_conversion_doc: "" });
  const [kids, setKids] = useState([newChild(defaultProgram)]);
  const [familyAns, setFamilyAns] = useState({});

  useEffect(() => {
    api.get("/child-program").then((r) => setCfg((c) => ({ ...c, ...r.data }))).catch(() => {});
  }, []);

  // Handle return from Stripe / PayPal
  useEffect(() => {
    const session = searchParams.get("child_session");
    const paypalToken = searchParams.get("child_pp") ? searchParams.get("token") : null;
    if (!session && !paypalToken) return;
    let tries = 0, cancelled = false;
    const poll = async () => {
      try {
        const { data } = paypalToken
          ? await api.post(`/child-registrations/paypal/capture/${paypalToken}`)
          : await api.get(`/child-registrations/status/${session}`);
        if (cancelled) return;
        if (data.reg_created || data.payment_status === "paid") {
          setPaidDone(true); setDone(true); setOpen(true);
          toast.success(t("child.pay_success"));
          ["child_session", "child_pp", "token", "PayerID"].forEach((k) => searchParams.delete(k));
          setSearchParams(searchParams, { replace: true });
          return;
        }
        if (tries >= 6) return;
      } catch (e) { if (tries >= 6) return; }
      tries += 1; setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tx = (key, i18nKey) => {
    const ov = cfg.texts?.[key];
    if (ov && typeof ov === "object") {
      const v = ov[lang] || ov.en;
      if (v && String(v).trim()) return v;
    } else if (ov && String(ov).trim()) {
      return ov; // legacy: a plain string was an all-language override
    }
    return t(i18nKey);
  };
  const cur = cfg.currency || "czk";
  const mode = cfg.payment_mode || (cfg.payment_enabled ? "required" : "none");
  const isRequired = mode === "required";
  const isSuggested = mode === "suggested";
  const showPrices = isRequired || isSuggested;
  const priceFor = (p) => (p === "sunday" ? cfg.price_sunday : p === "thursday" ? cfg.price_thursday : p === "both" ? cfg.price_both : 0);
  // Sibling discount: each additional Sunday child (after the first) is discounted.
  const siblingPrice = (base) => {
    if (!cfg.sibling_enabled || !(base > 0)) return base;
    const val = Number(cfg.sibling_value) || 0;
    if (cfg.sibling_type === "percent") return Math.round(base * (1 - Math.min(Math.max(val, 0), 100) / 100) * 100) / 100;
    if (cfg.sibling_type === "fixed_price") return Math.max(val, 0);
    if (cfg.sibling_type === "flat_off") return Math.max(base - val, 0);
    return base;
  };
  const fullTotal = kids.reduce((s, k) => s + (Number(priceFor(k.program)) || 0), 0);
  let _sundaySeen = 0;
  const computedTotal = kids.reduce((s, k) => {
    const base = Number(priceFor(k.program)) || 0;
    if (k.program === "sunday") { _sundaySeen += 1; return s + (_sundaySeen === 1 ? base : siblingPrice(base)); }
    return s + base;
  }, 0);
  const sundayCount = kids.filter((k) => k.program === "sunday").length;
  const siblingSavings = Math.max(0, Math.round((fullTotal - computedTotal) * 100) / 100);
  const hasSiblingDiscount = isRequired && cfg.sibling_enabled && sundayCount > 1 && siblingSavings > 0;
  const contribValue = contribTouched ? Math.max(0, Number(contribution) || 0) : (computedTotal > 0 ? computedTotal : 0);
  const presetBase = computedTotal > 0 ? computedTotal : 100;
  const presets = [...new Set([presetBase, presetBase * 2, presetBase * 3].map((n) => Math.round(n)))];
  const total = isRequired ? computedTotal : isSuggested ? contribValue : 0;
  const willPay = (isRequired && computedTotal > 0) || (isSuggested && contribValue > 0);
  const setContrib = (v) => { setContribution(v); setContribTouched(true); };
  const suggestedNote = cfg.suggested_note || {};
  const noteText = suggestedNote[lang] || suggestedNote.en || t("child.suggested_note_default");

  const allQuestions = cfg.questions || [];
  const familyQuestions = allQuestions.filter((q) => q.scope === "family");
  const childQuestions = allQuestions.filter((q) => (q.scope || "child") === "child");
  const setKidAnswer = (uid, qid, val) => setKids((arr) => arr.map((k) => (k.uid === uid ? { ...k, answers: { ...(k.answers || {}), [qid]: val } } : k)));

  const PROG_OPTS = [
    { value: "sunday", label: t("child.prog_sunday"), price: cfg.price_sunday, photo: cfg.photo_sunday },
  ];
  const showProgramPicker = PROG_OPTS.length > 1;

  // Thursday After-School: no longer a registration option, but offered on request via WhatsApp.
  const thursdayEnabled = cfg.thursday_enabled !== false;
  const thursdayNote = (cfg.thursday_note && (cfg.thursday_note[lang] || cfg.thursday_note.en)) || t("child.thursday_note");
  const waHref = `https://wa.me/${(t("contact.whatsapp") || "").replace(/\D/g, "")}`;
  const ThursdayCallout = () => (
    thursdayEnabled ? (
      <div data-testid="thursday-callout" className="rounded-2xl border border-[#BE923C]/30 bg-[#FBF7EE] p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <p className="font-serif text-lg text-[#0F172A]">{t("child.thursday_title")}</p>
            <p className="mt-1 text-sm text-[#475569] leading-relaxed">{thursdayNote}</p>
          </div>
          <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="thursday-whatsapp-cta"
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#a87f31] text-white text-sm font-semibold px-5 py-3 transition-colors">
            <MessageCircle size={16} /> {t("child.thursday_cta")}
          </a>
        </div>
      </div>
    ) : null
  );

  const JEWISH_OPTS = [
    { value: "birth", label: t("child.jewish_birth") },
    { value: "conversion", label: t("child.jewish_conversion") },
    { value: "na", label: t("child.jewish_na") },
  ];
  const JewishRadio = ({ value, onSelect, testid }) => (
    <div className="mt-1.5 flex flex-wrap gap-2" data-testid={testid}>
      {JEWISH_OPTS.map((o) => {
        const active = value === o.value;
        return (
          <button key={o.value} type="button" data-testid={`${testid}-${o.value}`} onClick={() => onSelect(o.value)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${active ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#475569] border-[#0F172A]/15 hover:border-[#BE923C]"}`}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
  const DocUploader = ({ value, onFile, busy, testid }) => (
    <div className="mt-2">
      <label data-testid={testid} className="inline-flex items-center gap-2 rounded-lg border border-[#BE923C]/40 bg-white px-3 py-2 text-xs font-semibold text-[#9E7829] cursor-pointer hover:bg-[#FBF7EE] transition-colors">
        {busy ? <Loader2 size={14} className="animate-spin" /> : value ? <Check size={14} /> : <Upload size={14} />}
        {busy ? t("child.uploading") : value ? t("child.change_file") : t("child.upload_file")}
        <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
      </label>
      {value && !busy && (
        <a href={resolveImg(value)} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-link`}
          className="ml-2 inline-flex items-center gap-1 text-xs text-[#BE923C] underline align-middle"><FileText size={12} /> {t("child.conversion_doc")}</a>
      )}
      <p className="mt-1 text-[11px] text-[#94A3B8]">{t("child.conversion_doc_hint")}</p>
    </div>
  );
  const ParentBlock = ({ role }) => {
    const nk = `${role}_name`, pk = `${role}_phone`, jk = `${role}_jewish`, dk = `${role}_conversion_doc`;
    return (
      <div data-testid={`parent-block-${role}`} className="rounded-2xl border border-[#0F172A]/8 bg-white p-4 space-y-3">
        <p className="text-sm font-bold text-[#0F172A]">{t(`child.${role}`)}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{t("child.parent_name")}<span className="text-[#BE923C]"> *</span></Label>
            <Input data-testid={`child-${role}-name`} value={parent[nk]} onChange={(e) => setP(nk, e.target.value)} className="mt-1.5 border-[#0F172A]/10" />
          </div>
          <div>
            <Label className="text-xs">{t("child.phone")}<span className="text-[#BE923C]"> *</span></Label>
            <Input data-testid={`child-${role}-phone`} value={parent[pk]} placeholder={t("child.phone_ph")} onChange={(e) => setP(pk, e.target.value)} className="mt-1.5 border-[#0F172A]/10" />
          </div>
        </div>
        {showJewish && (
          <div>
            <Label className="text-xs">{t("child.jewish_status")}<span className="text-[#BE923C]"> *</span></Label>
            {JewishRadio({ value: parent[jk], onSelect: (v) => setP(jk, v), testid: `child-${role}-jewish` })}
            {parent[jk] === "conversion" && DocUploader({ value: parent[dk], busy: uploadingDoc === role, testid: `child-${role}-doc`, onFile: (f) => onDoc(role, (url) => setP(dk, url), f) })}
          </div>
        )}
      </div>
    );
  };

  const setP = (k, v) => setParent((p) => ({ ...p, [k]: v }));
  const setKid = (uid, patch) => setKids((arr) => arr.map((k) => (k.uid === uid ? { ...k, ...patch } : k)));
  const addKid = () => setKids((arr) => [...arr, newChild(defaultProgram)]);
  const removeKid = (uid) => setKids((arr) => (arr.length > 1 ? arr.filter((k) => k.uid !== uid) : arr));

  const onPhoto = async (uid, file) => {
    if (!file) return;
    setUploadingUid(uid);
    try {
      const url = await uploadPublicFile(file);
      setKid(uid, { photo_url: url });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setUploadingUid(""); }
  };

  const showJewish = cfg.jewish_questions_enabled !== false;
  const motherConverted = showJewish && parent.mother_jewish === "conversion";

  // Document upload for parent (key on `parent`) or child (key = uid). `field` is the state key to set.
  const onDoc = async (busyKey, setter, file) => {
    if (!file) return;
    setUploadingDoc(busyKey);
    try {
      const res = await uploadDocument(file);
      setter(res.url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setUploadingDoc(""); }
  };

  const validate = () => {
    if (!parent.parent_email.trim()) { toast.error(t("child.err_parent")); return false; }
    if (showJewish) {
      const okParent = (name, phone, jw) => name.trim() && phone.trim() && jw;
      if (!okParent(parent.father_name, parent.father_phone, parent.father_jewish)
        || !okParent(parent.mother_name, parent.mother_phone, parent.mother_jewish)) {
        toast.error(t("child.err_parents")); return false;
      }
      if (parent.father_jewish === "conversion" && !parent.father_conversion_doc) { toast.error(t("child.err_conversion_doc")); return false; }
      if (parent.mother_jewish === "conversion" && !parent.mother_conversion_doc) { toast.error(t("child.err_conversion_doc")); return false; }
    } else {
      if (!parent.father_name.trim()) { toast.error(t("child.err_parents")); return false; }
    }
    for (const k of kids) {
      if (!k.name.trim() || !["sunday", "thursday", "both"].includes(k.program)) { toast.error(t("child.err_child")); return false; }
      if (motherConverted) {
        if (!k.jewish) { toast.error(t("child.err_child_jewish")); return false; }
        if (k.jewish === "conversion" && !k.conversion_doc) { toast.error(t("child.err_conversion_doc")); return false; }
      }
      for (const q of childQuestions) {
        if (q.required && !isAnswered(q, (k.answers || {})[q.id])) {
          toast.error(`${(q.label && (q.label[lang] || q.label.en)) || t("child.err_child")}`); return false;
        }
      }
    }
    for (const q of familyQuestions) {
      if (q.required && !isAnswered(q, familyAns[q.id])) {
        toast.error(`${(q.label && (q.label[lang] || q.label.en)) || t("child.err_child")}`); return false;
      }
    }
    return true;
  };

  const payload = () => ({
    parent_name: [parent.father_name, parent.mother_name].filter(Boolean).join(" & ") || parent.father_name,
    parent_email: parent.parent_email,
    parent_phone: parent.father_phone, parent_phone2: parent.mother_phone, parent_address: parent.parent_address,
    father_name: parent.father_name, father_phone: parent.father_phone,
    father_jewish: showJewish ? parent.father_jewish : "", father_conversion_doc: showJewish ? parent.father_conversion_doc : "",
    mother_name: parent.mother_name, mother_phone: parent.mother_phone,
    mother_jewish: showJewish ? parent.mother_jewish : "", mother_conversion_doc: showJewish ? parent.mother_conversion_doc : "",
    photo_consent: !!parent.photo_consent,
    children: kids.map(({ uid, jewish, conversion_doc, answers, ...rest }) => {
      const base = motherConverted ? { ...rest, jewish, conversion_doc: jewish === "conversion" ? conversion_doc : "" } : rest;
      return { ...base, answers: buildAnswers(childQuestions, answers || {}) };
    }),
    answers: buildAnswers(familyQuestions, familyAns),
    lang,
  });

  const submit = async (e, skipContribution = false) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const goPaid = !skipContribution && willPay;
      if (goPaid) {
        setPayLoading("stripe");
        const { data } = await api.post("/child-registrations/checkout", {
          ...payload(), amount: isSuggested ? contribValue : undefined,
          origin_url: window.location.origin, return_path: returnPath || window.location.pathname,
        });
        window.location.href = data.checkout_url;
        return;
      }
      await api.post("/child-registrations", payload());
      setDone(true);
      toast.success(t("child.success_title"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setSubmitting(false); setPayLoading("");
    }
  };

  const payPaypal = async () => {
    if (!validate()) return;
    setSubmitting(true); setPayLoading("paypal");
    try {
      const { data } = await api.post("/child-registrations/paypal/create", {
        ...payload(), amount: isSuggested ? contribValue : undefined,
        origin_url: window.location.origin, return_path: returnPath || window.location.pathname,
      });
      window.location.href = data.approve_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setSubmitting(false); setPayLoading("");
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => {
      setDone(false); setPaidDone(false); setSubmitting(false); setPayLoading("");
      setContribution(0); setContribTouched(false);
      setParent({ parent_email: "", parent_address: "", photo_consent: false, father_name: "", father_phone: "", father_jewish: "", father_conversion_doc: "", mother_name: "", mother_phone: "", mother_jewish: "", mother_conversion_doc: "" });
      setKids([newChild(defaultProgram)]);
      setFamilyAns({});
    }, 250);
  };

  return (
    <section className={compact ? "" : "relative z-10 py-14 sm:py-16"} data-testid="child-program-section">
      <div className={compact ? "" : "mx-auto max-w-4xl px-5 sm:px-8"}>
        <div className="relative overflow-hidden rounded-3xl bg-[#0F172A] text-white p-8 sm:p-12 shadow-[0_20px_60px_rgba(15,23,42,0.18)]">
          <div className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-[#BE923C]/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-[#BE923C]/15 blur-3xl" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8">
            <div className="flex-1">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#E7C77E]">
                <Baby size={14} /> {tx("signup_title", "child.signup_title")}
              </span>
              <p className="mt-4 text-lg sm:text-xl leading-relaxed text-white/85">{tx("signup_desc", "child.signup_desc")}</p>
            </div>
            <button
              data-testid="child-signup-open"
              onClick={() => setOpen(true)}
              className="shrink-0 rounded-full bg-gradient-to-r from-[#E7C77E] to-[#BE923C] px-8 py-4 text-sm font-bold text-[#0F172A] shadow-[0_10px_30px_rgba(190,146,60,0.4)] hover:shadow-[0_14px_40px_rgba(190,146,60,0.55)] hover:-translate-y-0.5 transition-all"
            >
              {tx("signup_btn", "child.signup_btn")}
            </button>
          </div>
        </div>
        {thursdayEnabled && <div className="mt-4"><ThursdayCallout /></div>}
      </div>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0 gap-0" data-testid="child-signup-dialog">
          <DialogHeader className="relative bg-[#0F172A] text-white px-6 py-7 overflow-hidden text-start">
            <div className="pointer-events-none absolute -top-16 -right-10 h-48 w-48 rounded-full bg-[#BE923C]/25 blur-3xl" />
            <span className="relative inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#E7C77E] w-fit">
              <Baby size={14} /> {tx("signup_title", "child.signup_title")}
            </span>
            <DialogTitle className="relative font-serif text-2xl sm:text-3xl mt-3">{tx("form_title", "child.form_title")}</DialogTitle>
            <DialogDescription className="relative text-white/70 text-sm mt-1">{tx("form_intro", "child.form_intro")}</DialogDescription>
          </DialogHeader>

          <div className="px-6 py-6">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div key="done" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center text-center py-6" data-testid="child-signup-success">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#EEF4EC] text-[#4b7a43]"><CheckCircle2 size={34} /></div>
                  <p className="mt-4 text-xl font-semibold text-[#0F172A]">{t("child.success_title")}</p>
                  <p className="mt-2 text-sm text-[#475569] max-w-sm">{paidDone ? t("child.pay_success") : t("child.success_desc")}</p>
                  <button onClick={close} data-testid="child-signup-close"
                    className="mt-6 rounded-full bg-[#0F172A] text-white text-sm font-semibold px-6 py-3 hover:bg-[#1e293b] transition-colors">
                    <X size={15} className="inline mr-1.5 -mt-0.5" /> {t("program.back")}
                  </button>
                </motion.div>
              ) : (
                <motion.form key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onSubmit={submit} className="space-y-8">
                  {thursdayEnabled && <ThursdayCallout />}

                  {/* Parent details */}
                  <div className="space-y-3">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#BE923C]">{t("child.parent_section")}</p>
                    {ParentBlock({ role: "father" })}
                    {ParentBlock({ role: "mother" })}
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">{t("child.email")}<span className="text-[#BE923C]"> *</span></Label>
                        <Input data-testid="child-parent-email" type="email" value={parent.parent_email} placeholder={t("child.email_ph")}
                          onChange={(e) => setP("parent_email", e.target.value)} className="mt-1.5 border-[#0F172A]/10" />
                      </div>
                      <div>
                        <Label className="text-xs">{t("child.address")}</Label>
                        <Input data-testid="child-parent-address" value={parent.parent_address} placeholder={t("child.address_ph")}
                          onChange={(e) => setP("parent_address", e.target.value)} className="mt-1.5 border-[#0F172A]/10" />
                      </div>
                    </div>
                  </div>

                  {/* Children */}
                  <div className="space-y-4">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#BE923C]">{t("child.children_section")}</p>
                    {kids.map((k, i) => {
                      const age = ageFromDob(k.dob);
                      return (
                        <div key={k.uid} data-testid={`child-row-${i}`} className="rounded-3xl border border-[#BE923C]/25 bg-[#FBF7EE] p-5">
                          <div className="flex items-center justify-between mb-4">
                            <span className="inline-flex items-center gap-2 text-sm font-bold text-[#0F172A]">
                              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#BE923C] text-white text-xs">{i + 1}</span>
                              {t("child.child")} {i + 1}
                            </span>
                            {kids.length > 1 && (
                              <button type="button" data-testid={`child-remove-${i}`} onClick={() => removeKid(k.uid)}
                                className="p-2 rounded-xl hover:bg-red-50 text-red-500 transition-colors"><Trash2 size={16} /></button>
                            )}
                          </div>

                          <div className="flex flex-col sm:flex-row gap-5">
                            {/* Avatar */}
                            <div className="shrink-0 flex justify-center">
                              <label data-testid={`child-photo-${i}`} className="group relative flex h-24 w-24 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-[#BE923C]/60 bg-white transition-colors hover:border-[#BE923C]">
                                {uploadingUid === k.uid ? (
                                  <Loader2 size={22} className="animate-spin text-[#BE923C]" />
                                ) : k.photo_url ? (
                                  <img src={resolveImg(k.photo_url)} alt="" className="h-full w-full object-cover" />
                                ) : (
                                  <span className="flex flex-col items-center text-[#BE923C]"><Camera size={22} /><span className="mt-1 text-[10px] font-semibold">{t("child.photo_add")}</span></span>
                                )}
                                {k.photo_url && uploadingUid !== k.uid && (
                                  <span className="absolute inset-x-0 bottom-0 hidden bg-black/40 py-1 text-center text-[10px] font-semibold text-white group-hover:block">{t("child.photo_change")}</span>
                                )}
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => onPhoto(k.uid, e.target.files?.[0])} />
                              </label>
                            </div>

                            {/* Fields */}
                            <div className="flex-1 space-y-3">
                              <div className="grid sm:grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-xs">{t("child.child_name")}<span className="text-[#BE923C]"> *</span></Label>
                                  <Input data-testid={`child-name-${i}`} value={k.name} placeholder={t("child.child_name_ph")}
                                    onChange={(e) => setKid(k.uid, { name: e.target.value })} className="mt-1 border-[#0F172A]/10 bg-white" />
                                </div>
                                <div>
                                  <div className="flex items-center justify-between gap-2">
                                    <Label className="text-xs">{t("child.dob")}</Label>
                                    {age !== null && (
                                      <span data-testid={`child-age-${i}`} className="rounded-full bg-[#EEF4EC] px-2 py-0.5 text-[10px] font-bold text-[#4b7a43]">{t("child.age")} {age}</span>
                                    )}
                                  </div>
                                  <Input data-testid={`child-dob-${i}`} type="date" value={k.dob}
                                    onChange={(e) => setKid(k.uid, { dob: e.target.value })} className="mt-1 border-[#0F172A]/10 bg-white" />
                                </div>
                              </div>
                              <div className="grid sm:grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-xs">{t("child.gender")}</Label>
                                  <div className="mt-1 grid grid-cols-2 gap-2">
                                    {[["boy", t("child.boy")], ["girl", t("child.girl")]].map(([val, lbl]) => (
                                      <button key={val} type="button" data-testid={`child-gender-${i}-${val}`} onClick={() => setKid(k.uid, { gender: val })}
                                        className={`rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${k.gender === val ? "bg-[#BE923C] text-white border-[#BE923C]" : "bg-white text-[#0F172A] border-[#0F172A]/10 hover:border-[#BE923C]"}`}>
                                        {lbl}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-xs">{t("child.grade")}</Label>
                                  <Input data-testid={`child-grade-${i}`} value={k.grade} placeholder={t("child.grade_ph")}
                                    onChange={(e) => setKid(k.uid, { grade: e.target.value })} className="mt-1 border-[#0F172A]/10 bg-white" />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Program picture cards */}
                          {showProgramPicker && (
                          <div className="mt-4">
                            <Label className="text-xs">{t("child.program")}<span className="text-[#BE923C]"> *</span></Label>
                            <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                              {PROG_OPTS.map((o) => {
                                const active = k.program === o.value;
                                return (
                                  <button key={o.value} type="button" data-testid={`child-program-${i}-${o.value}`} onClick={() => setKid(k.uid, { program: o.value })}
                                    className={`group relative overflow-hidden rounded-2xl border-2 text-start transition-all duration-300 ${active ? "border-[#0F172A] shadow-[0_12px_30px_rgba(15,23,42,0.20)] -translate-y-0.5" : "border-transparent hover:border-[#BE923C]/40 hover:-translate-y-0.5"}`}>
                                    <div className="relative h-24 w-full overflow-hidden bg-[#0F172A]/5">
                                      {o.photo && <img src={resolveImg(o.photo)} alt={o.label} className={`h-full w-full object-cover transition-transform duration-500 ${active ? "scale-105" : "group-hover:scale-105"}`} />}
                                      <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
                                      {showPrices && Number(o.price) > 0 && (
                                        <span className="absolute top-2 right-2 rounded-full bg-white/90 px-2.5 py-0.5 text-xs font-bold text-[#9E7829]">{money(o.price, cur)}</span>
                                      )}
                                      {active && (
                                        <span className="absolute top-2 left-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#BE923C] text-white shadow"><Check size={14} strokeWidth={3} /></span>
                                      )}
                                    </div>
                                    <div className={`px-3 py-2 text-sm font-semibold transition-colors ${active ? "bg-[#0F172A] text-white" : "bg-white text-[#0F172A]"}`}>{o.label}</div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          )}

                          <div className="mt-3">
                            <Label className="text-xs">{t("child.allergies")}</Label>
                            <Textarea data-testid={`child-allergies-${i}`} rows={2} value={k.allergies} placeholder={t("child.allergies_ph")}
                              onChange={(e) => setKid(k.uid, { allergies: e.target.value })} className="mt-1 border-[#0F172A]/10 bg-white" />
                          </div>
                          {childQuestions.length > 0 && (
                            <div className="mt-3 space-y-3">
                              {childQuestions.map((q) => (
                                <QuestionField key={q.id} q={q} lang={lang} idPrefix={`child-${i}`}
                                  value={(k.answers || {})[q.id]} onChange={(val) => setKidAnswer(k.uid, q.id, val)} />
                              ))}
                            </div>
                          )}
                          {cfg.russian_group_enabled !== false && childSpeaksRussian(childQuestions, k.answers) && (
                            <div className="mt-3 rounded-xl border border-[#BE923C]/25 bg-white p-3" data-testid={`child-russian-group-${i}`}>
                              <Label className="text-xs">{RU_GROUP_Q[lang] || RU_GROUP_Q.en}</Label>
                              <div className="mt-1.5 flex gap-2">
                                {["yes", "no"].map((yn) => (
                                  <button key={yn} type="button" data-testid={`child-russian-group-${i}-${yn}`}
                                    onClick={() => setKid(k.uid, { russian_group: yn })}
                                    className={`rounded-xl border px-4 py-2 text-sm font-semibold transition-colors ${k.russian_group === yn ? "bg-[#BE923C] text-white border-[#BE923C]" : "bg-white text-[#0F172A] border-[#0F172A]/10 hover:border-[#BE923C]"}`}>
                                    {YESNO[yn][lang] || YESNO[yn].en}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {(() => {
                            const speaksRu = childSpeaksRussian(childQuestions, k.answers);
                            const g = allocateGroup(cfg, k, speaksRu);
                            if (!g.name && !g.review) return null;
                            return (
                              <div className="mt-3 flex items-center gap-2 rounded-xl bg-[#0F172A] px-3 py-2 text-sm text-white" data-testid={`child-group-${i}`}>
                                <span className="font-semibold text-[#E9C77E]">{GROUP_LABEL[lang] || GROUP_LABEL.en}:</span>
                                <span>{g.review ? (GROUP_REVIEW[lang] || GROUP_REVIEW.en) : g.name}</span>
                              </div>
                            );
                          })()}
                          {motherConverted && (
                            <div data-testid={`child-jewish-block-${i}`} className="mt-3 rounded-xl border border-[#BE923C]/25 bg-white p-3">
                              <Label className="text-xs">{t("child.jewish_status")}<span className="text-[#BE923C]"> *</span></Label>
                              {JewishRadio({ value: k.jewish, onSelect: (v) => setKid(k.uid, { jewish: v }), testid: `child-jewish-${i}` })}
                              {k.jewish === "conversion" && DocUploader({ value: k.conversion_doc, busy: uploadingDoc === k.uid, testid: `child-doc-${i}`, onFile: (f) => onDoc(k.uid, (url) => setKid(k.uid, { conversion_doc: url }), f) })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <button type="button" data-testid="child-add" onClick={addKid}
                      className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/15 px-5 py-2.5 text-sm font-semibold hover:border-[#BE923C] hover:-translate-y-0.5 transition-all">
                      <Plus size={16} /> {t("child.add_child")}
                    </button>
                  </div>

                  {/* Additional family-wide questions */}
                  {familyQuestions.length > 0 && (
                    <div className="space-y-3" data-testid="family-questions">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#BE923C]">{{ en: "A few more questions", cs: "Ještě několik otázek", he: "עוד כמה שאלות", ru: "Ещё несколько вопросов" }[lang]}</p>
                      <div className="rounded-2xl border border-[#0F172A]/8 bg-white p-4 space-y-4">
                        {familyQuestions.map((q) => (
                          <QuestionField key={q.id} q={q} lang={lang} idPrefix="family"
                            value={familyAns[q.id]} onChange={(val) => setFamilyAns((a) => ({ ...a, [q.id]: val }))} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Payment — required total */}
                  {isRequired && (
                    <div className="rounded-2xl bg-[#FBF7EE] border border-[#BE923C]/25 p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#475569]">{t("child.total")}</span>
                        <span className="flex items-baseline gap-2">
                          {hasSiblingDiscount && <span className="text-sm text-[#94A3B8] line-through">{money(fullTotal, cur)}</span>}
                          <span data-testid="child-total" className="font-serif text-2xl font-semibold text-[#BE923C]">
                            {computedTotal > 0 ? money(computedTotal, cur) : t("child.free")}
                          </span>
                        </span>
                      </div>
                      {hasSiblingDiscount && (
                        <div data-testid="child-sibling-discount" className="flex items-center gap-2 rounded-xl bg-white/70 px-3 py-2 text-sm text-[#9E7829]">
                          <BadgePercent size={15} className="shrink-0" />
                          <span>
                            {{ en: `Sibling discount applied — you save ${money(siblingSavings, cur)}`,
                               he: `הנחת אחים — חסכתם ${money(siblingSavings, cur)}`,
                               ru: `Скидка для братьев и сестёр — вы экономите ${money(siblingSavings, cur)}` }[lang]
                             || `Sibling discount applied — you save ${money(siblingSavings, cur)}`}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment — suggested participation */}
                  {isSuggested && (
                    <div className="rounded-2xl bg-[#FBF7EE] border border-[#BE923C]/25 p-4 space-y-3" data-testid="child-suggested-box">
                      <p className="text-sm text-[#0F172A] font-medium" data-testid="child-suggested-note">{noteText}</p>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">{t("child.suggested_label")}</Label>
                        {computedTotal > 0 && <span className="text-xs text-[#9E7829]" data-testid="child-suggested-amount">{t("child.total")}: {money(computedTotal, cur)}</span>}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {presets.map((p) => (
                          <button key={p} type="button" data-testid={`child-contrib-preset-${p}`} onClick={() => setContrib(p)}
                            className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${Math.round(contribValue) === p ? "bg-[#BE923C] text-white border-[#BE923C]" : "bg-white text-[#0F172A] border-[#0F172A]/10 hover:border-[#BE923C]"}`}>
                            {money(p, cur)}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <Input type="number" min={0} step="1" data-testid="child-contrib-amount" value={contribValue}
                          onChange={(e) => setContrib(Number(e.target.value) || 0)} className="border-[#0F172A]/10 bg-white max-w-[160px]" />
                        <span className="text-sm text-[#475569] uppercase">{cur}</span>
                      </div>
                      <p className="text-xs text-[#475569]">{t("child.suggested_hint")}</p>
                    </div>
                  )}

                  <label data-testid="child-photo-consent-label" className="flex items-start gap-2.5 text-xs text-[#475569] leading-relaxed cursor-pointer">
                    <input type="checkbox" data-testid="child-photo-consent" checked={!!parent.photo_consent}
                      onChange={(e) => setParent((p) => ({ ...p, photo_consent: e.target.checked }))}
                      className="mt-0.5 w-4 h-4 accent-[#BE923C] shrink-0" />
                    <span>{t("child.photo_consent")}</span>
                  </label>

                  <button type="submit" data-testid="child-submit" disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#E7C77E] to-[#BE923C] px-8 py-4 text-sm font-bold text-[#0F172A] shadow-[0_10px_30px_rgba(190,146,60,0.35)] hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:translate-y-0">
                    {willPay && <CreditCard size={18} />}
                    {submitting && payLoading !== "paypal"
                      ? (willPay ? t("child.redirecting") : t("child.submitting"))
                      : isRequired && computedTotal > 0 ? `${t("child.pay")} · ${money(computedTotal, cur)}`
                      : isSuggested && contribValue > 0 ? `${t("child.contribute")} · ${money(contribValue, cur)}`
                      : t("child.submit")}
                  </button>

                  {willPay && cfg.paypal_enabled && (
                    <>
                      <div className="flex items-center gap-3 text-xs text-[#94A3B8]">
                        <span className="h-px flex-1 bg-[#0F172A]/10" /> {t("donate.or")} <span className="h-px flex-1 bg-[#0F172A]/10" />
                      </div>
                      <button type="button" data-testid="child-paypal" onClick={payPaypal} disabled={submitting}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FFC439] hover:bg-[#f0b62d] text-[#003087] font-bold py-3.5 transition-colors disabled:opacity-60">
                        {payLoading === "paypal" ? t("child.redirecting") : <span className="font-extrabold italic">Pay<span className="text-[#0070E0]">Pal</span></span>}
                      </button>
                    </>
                  )}

                  {isSuggested && contribValue > 0 && (
                    <button type="button" data-testid="child-without-btn" onClick={(e) => submit(e, true)} disabled={submitting}
                      className="w-full text-sm text-[#475569] hover:text-[#0F172A] underline underline-offset-2 disabled:opacity-60">
                      {t("child.without")}
                    </button>
                  )}
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
